/**
 * Array modifier — pure typed-array core (Three.js-free).
 *
 * Creates N copies of the geometry, each offset by a cumulative delta.
 * The delta per copy is the sum of:
 *   - Relative offset: a fraction of the bounding-box extent per axis
 *   - Constant offset: a fixed displacement in object space
 *   - Object Offset: relative TRS matrix (inv(owner) × offset_obj)
 *
 * All modes are combined into a single per-copy delta matrix that is
 * raised to successive powers (M^0, M^1, … M^(count-1)), matching
 * Blender's MOD_ARR_OFF_RELATIVE | MOD_ARR_OFF_CONST | MOD_ARR_OFF_OBJ
 * behaviour.
 *
 * Morph deltas, skin weights, and sculpt displacements are position-
 * independent and are copied verbatim for every instance.
 */

import { clampArrayModifierCount } from "../../modeling/modifierLimits.ts";
import { asSharedTriangleIndices } from "../../modeling/types.ts";

// ---------------------------------------------------------------------------
// Minimal 4×4 column-major matrix helpers (no Three.js dependency)
// ---------------------------------------------------------------------------

/** Identity 4×4 matrix (column-major). */
function mat4Identity(): number[] {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

/** Multiply two column-major 4×4 matrices: result = a × b */
function mat4Mul(a: number[], b: number[]): number[] {
  const out = new Array<number>(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) {
        sum += a[k * 4 + row] * b[col * 4 + k];
      }
      out[col * 4 + row] = sum;
    }
  }
  return out;
}

/** Transform a point by a column-major 4×4 matrix (w=1). */
function transformPoint(
  m: number[],
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  return [
    m[0] * x + m[4] * y + m[8] * z + m[12],
    m[1] * x + m[5] * y + m[9] * z + m[13],
    m[2] * x + m[6] * y + m[10] * z + m[14],
  ];
}

/** Transform a direction vector by the upper-left 3×3 of a 4×4 matrix (no translation). */
function transformDir(
  m: number[],
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  return [
    m[0] * x + m[4] * y + m[8] * z,
    m[1] * x + m[5] * y + m[9] * z,
    m[2] * x + m[6] * y + m[10] * z,
  ];
}

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import { provenanceFromSourceMap } from "../../modeling/provenance.ts";
import { buildTopologyWithExplicitEdges } from "../../modeling/topology.ts";
import type { PolygonTopology } from "../../modeling/types.ts";
import type { GeometryGroup, ModifierGeometry } from "./modifierTypes.ts";

export function arrayCore(
  geo: ModifierGeometry,
  mod: Extract<BlendModifier, { kind: "array" }>,
): ModifierGeometry {
  const count = clampArrayModifierCount(mod.count);
  if (count <= 1) return geo;

  const positions = geo.positions;
  const vertCount = positions.length / 3;
  const indices = geo.indices;
  const indexCount = indices.length;

  // ── Build per-copy delta matrix ───────────────────────────────────────────
  // Start from the object offset matrix (relative transform) or identity,
  // then add relative and constant offsets to its translation component.
  // This combined matrix is raised to power i for copy i, matching Blender.
  const delta = mod.offsetMatrix ? [...mod.offsetMatrix] : mat4Identity();

  if (mod.useRelativeOffset) {
    let minX = Infinity,
      minY = Infinity,
      minZ = Infinity;
    let maxX = -Infinity,
      maxY = -Infinity,
      maxZ = -Infinity;
    for (let i = 0; i < vertCount; i++) {
      const b = i * 3;
      const x = positions[b],
        y = positions[b + 1],
        z = positions[b + 2];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    delta[12] += mod.relativeOffset[0] * (maxX - minX);
    delta[13] += mod.relativeOffset[1] * (maxY - minY);
    delta[14] += mod.relativeOffset[2] * (maxZ - minZ);
  }

  if (mod.useConstantOffset) {
    delta[12] += mod.constantOffset[0];
    delta[13] += mod.constantOffset[1];
    delta[14] += mod.constantOffset[2];
  }

  // ── Allocate output arrays ─────────────────────────────────────────────────
  const totalVerts = vertCount * count;
  const totalIndices = indexCount * count;

  const newPos = new Float32Array(totalVerts * 3);

  const hasNormals = geo.normals !== null;
  const newNorm = hasNormals ? new Float32Array(totalVerts * 3) : null;

  const hasUVs = geo.uvs !== null;
  const newUVs = hasUVs ? new Float32Array(totalVerts * 2) : null;

  const hasSkin = geo.skinIndex !== null && geo.skinWeight !== null;
  const newSkinIndex = hasSkin ? new Float32Array(totalVerts * 4) : null;
  const newSkinWeight = hasSkin ? new Float32Array(totalVerts * 4) : null;

  const hasSculpt = geo.sculptDisplacement !== null;
  const newSculpt = hasSculpt ? new Float32Array(totalVerts * 3) : null;

  const newMorphArrays = geo.morphArrays.map(
    () => new Float32Array(totalVerts * 3),
  );
  const newIndices = asSharedTriangleIndices(new Uint32Array(totalIndices));

  // ── Build per-copy cumulative matrices (delta^0, delta^1, … delta^(count-1))
  const mats: number[][] = [mat4Identity()];
  for (let i = 1; i < count; i++) {
    mats.push(mat4Mul(mats[i - 1], delta));
  }

  // Check if any matrix has a non-identity upper-left 3×3 (rotation/scale).
  // If all copies are pure translation, we can skip normal transformation.
  const hasRotation = mats.some(
    (m) =>
      Math.abs(m[0] - 1) > 1e-6 ||
      Math.abs(m[1]) > 1e-6 ||
      Math.abs(m[2]) > 1e-6 ||
      Math.abs(m[4]) > 1e-6 ||
      Math.abs(m[5] - 1) > 1e-6 ||
      Math.abs(m[6]) > 1e-6 ||
      Math.abs(m[8]) > 1e-6 ||
      Math.abs(m[9]) > 1e-6 ||
      Math.abs(m[10] - 1) > 1e-6,
  );

  // ── Fill per-copy data ─────────────────────────────────────────────────────
  for (let c = 0; c < count; c++) {
    const posOut = c * vertCount * 3;
    const mat = mats[c];

    // Positions — apply cumulative delta matrix
    for (let v = 0; v < vertCount; v++) {
      const src = v * 3;
      const dst = posOut + src;
      const [px, py, pz] = transformPoint(
        mat,
        positions[src],
        positions[src + 1],
        positions[src + 2],
      );
      newPos[dst] = px;
      newPos[dst + 1] = py;
      newPos[dst + 2] = pz;
    }

    // Normals — rotate by the upper-left 3×3, then re-normalize
    if (newNorm && geo.normals) {
      if (hasRotation && c > 0) {
        for (let v = 0; v < vertCount; v++) {
          const src = v * 3;
          const dst = posOut + src;
          let [nx, ny, nz] = transformDir(
            mat,
            geo.normals[src],
            geo.normals[src + 1],
            geo.normals[src + 2],
          );
          const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
          if (len > 1e-8) {
            nx /= len;
            ny /= len;
            nz /= len;
          }
          newNorm[dst] = nx;
          newNorm[dst + 1] = ny;
          newNorm[dst + 2] = nz;
        }
      } else {
        newNorm.set(geo.normals, posOut);
      }
    }

    // UVs — texture coords, no translation
    if (newUVs && geo.uvs) {
      newUVs.set(geo.uvs, c * vertCount * 2);
    }

    // Morph deltas — relative displacements, no translation
    for (let m = 0; m < geo.morphArrays.length; m++) {
      newMorphArrays[m].set(geo.morphArrays[m], posOut);
    }

    // Skin weights — bone influence data, no translation
    if (newSkinIndex && newSkinWeight && geo.skinIndex && geo.skinWeight) {
      newSkinIndex.set(geo.skinIndex, c * vertCount * 4);
      newSkinWeight.set(geo.skinWeight, c * vertCount * 4);
    }

    // Sculpt displacement — per-vertex deltas, no translation
    if (newSculpt && geo.sculptDisplacement) {
      newSculpt.set(geo.sculptDisplacement, posOut);
    }

    // Indices — remap to this copy's vertex range
    const vertOffset = c * vertCount;
    const idxBase = c * indexCount;
    for (let i = 0; i < indexCount; i++) {
      newIndices[idxBase + i] = indices[i] + vertOffset;
    }
  }

  // ── Duplicate material groups across copies ────────────────────────────────
  const newGroups: GeometryGroup[] = [];
  if (geo.groups.length > 0) {
    for (let c = 0; c < count; c++) {
      const idxOffset = c * indexCount;
      for (const g of geo.groups) {
        newGroups.push({
          start: g.start + idxOffset,
          count: g.count,
          materialIndex: g.materialIndex,
        });
      }
    }
  }

  // Replicate wire edges per copy with per-copy vertex offset. Creases
  // travel alongside flags — each duplicated edge inherits its source
  // weight verbatim.
  let newEdges: Uint32Array | null = null;
  let newEdgeFlags: Uint8Array | null = null;
  let newEdgeCreases: Float32Array | null = null;
  if (geo.edges && geo.edges.length > 0 && geo.edgeFlags) {
    const srcEdgeCount = geo.edges.length / 2;
    newEdges = new Uint32Array(srcEdgeCount * count * 2);
    newEdgeFlags = new Uint8Array(srcEdgeCount * count);
    const srcCreases = geo.edgeCreases ?? null;
    if (srcCreases) newEdgeCreases = new Float32Array(srcEdgeCount * count);
    for (let c = 0; c < count; c++) {
      const vertOff = c * vertCount;
      const edgeBase = c * srcEdgeCount;
      for (let i = 0; i < srcEdgeCount; i++) {
        newEdges[(edgeBase + i) * 2] = geo.edges[i * 2] + vertOff;
        newEdges[(edgeBase + i) * 2 + 1] = geo.edges[i * 2 + 1] + vertOff;
        newEdgeFlags[edgeBase + i] = geo.edgeFlags[i];
        if (newEdgeCreases && srcCreases)
          newEdgeCreases[edgeBase + i] = srcCreases[i];
      }
    }
  }

  // Replicate skin radii verbatim for every copy (scale is position-independent)
  let newSkinRadii: Float32Array | null = null;
  if (geo.skinRadii) {
    newSkinRadii = new Float32Array(totalVerts);
    for (let c = 0; c < count; c++) {
      newSkinRadii.set(geo.skinRadii, c * vertCount);
    }
  }

  // Replicate polygon topology: each copy offsets corner vertex indices by
  // c * vertCount, preserving face structure across all instances.
  let newTopology: PolygonTopology | undefined;
  if (geo.topology) {
    const { offsets, corners } = geo.topology;
    const faceCount = offsets.length - 1;
    const srcCornerCount = corners.length;
    const newFaceCount = faceCount * count;
    const newCornerCount = srcCornerCount * count;

    const newOffsets = new Uint32Array(newFaceCount + 1);
    const newCorners = new Uint32Array(newCornerCount);

    for (let c = 0; c < count; c++) {
      const faceBase = c * faceCount;
      const cornerBase = c * srcCornerCount;
      const vertOffset = c * vertCount;
      for (let f = 0; f < faceCount; f++) {
        newOffsets[faceBase + f] = cornerBase + offsets[f];
      }
      for (let i = 0; i < srcCornerCount; i++) {
        newCorners[cornerBase + i] = corners[i] + vertOffset;
      }
    }
    newOffsets[newFaceCount] = newCornerCount;

    newTopology = buildTopologyWithExplicitEdges(
      newOffsets,
      newCorners,
      newEdges ?? new Uint32Array(0),
      newEdgeFlags ?? new Uint8Array(0),
      newEdgeCreases,
    );
    newEdges = newTopology.edges;
    newEdgeFlags = newTopology.edgeFlags;
    newEdgeCreases = newTopology.edgeCreases ?? null;
  }

  // Provenance: every copy is a plain translation/rotation of the source
  // vert cloud, so output vert `c * vertCount + v` descends from source
  // vert `v`. Populate a source-map and build the standard 1:1-parent
  // provenance.
  const srcForNew = new Int32Array(totalVerts);
  for (let c = 0; c < count; c++) {
    for (let v = 0; v < vertCount; v++) {
      srcForNew[c * vertCount + v] = v;
    }
  }
  const provenance = provenanceFromSourceMap(vertCount, srcForNew);

  return {
    positions: newPos,
    indices: newIndices,
    normals: newNorm,
    uvs: newUVs,
    morphArrays: newMorphArrays,
    skinIndex: newSkinIndex,
    skinWeight: newSkinWeight,
    sculptDisplacement: newSculpt,
    groups: newGroups,
    morphTargetsRelative: geo.morphTargetsRelative,
    morphNames: geo.morphNames,
    edges: newEdges,
    edgeFlags: newEdgeFlags,
    edgeCreases: newEdgeCreases,
    skinRadii: newSkinRadii,
    topology: newTopology,
    provenance,
  };
}
